import React, { useState, createContext, useContext } from 'react';
export type TakeawayPackage = {
  id: string;
  name: string;
  description: string;
  price: number;
  active: boolean;
};
type Ctx = {
  packages: TakeawayPackage[];
  addPackage: (p: Omit<TakeawayPackage, 'id'>) => void;
  updatePackage: (id: string, patch: Partial<TakeawayPackage>) => void;
  deletePackage: (id: string) => void;
  getPackage: (id: string | undefined) => TakeawayPackage | undefined;
};
const TakeawayPackagesContext = createContext<Ctx | null>(null);
const uid = () => Math.random().toString(36).slice(2, 9);
const seed: TakeawayPackage[] = [{
  id: 'pkg_standard',
  name: 'Standard meal box',
  description: 'Cardboard clamshell for sandwiches, plates, and most entrees.',
  price: 0.75,
  active: true
}, {
  id: 'pkg_soup',
  name: 'Soup container',
  description: 'Insulated 16oz cup with lid. Used for soups, stews, and hot liquids.',
  price: 0.5,
  active: true
}, {
  id: 'pkg_wrap',
  name: 'Wrap pouch',
  description: 'Foil-lined pouch to keep wraps and burritos warm.',
  price: 0.3,
  active: true
}, {
  id: 'pkg_premium',
  name: 'Premium platter',
  description: 'Large compostable platter for sharing plates and family meals.',
  price: 1.5,
  active: true
}];
export function TakeawayPackagesProvider({
  children
}: {
  children: ReactNode;
}) {
  const [packages, setPackages] = useState<TakeawayPackage[]>(seed);
  const addPackage = (p: Omit<TakeawayPackage, 'id'>) => {
    setPackages(prev => [...prev, {
      ...p,
      id: `pkg_${uid()}`
    }]);
  };
  const updatePackage = (id: string, patch: Partial<TakeawayPackage>) => {
    setPackages(prev => prev.map(p => p.id === id ? {
      ...p,
      ...patch
    } : p));
  };
  const deletePackage = (id: string) => {
    setPackages(prev => prev.filter(p => p.id !== id));
  };
  const getPackage = (id: string | undefined) => id ? packages.find(p => p.id === id) : undefined;
  return <TakeawayPackagesContext.Provider value={{
    packages,
    addPackage,
    updatePackage,
    deletePackage,
    getPackage
  }} data-id="element-3584">
      {children}
    </TakeawayPackagesContext.Provider>;
}
export function useTakeawayPackages() {
  const ctx = useContext(TakeawayPackagesContext);
  if (!ctx) throw new Error('useTakeawayPackages must be used within TakeawayPackagesProvider');
  return ctx;
}